/**
 * Single source of truth for currency symbols, names and decimal precision.
 *
 * Precision rules (market standard):
 *  - Fiat: ISO 4217 minor units (JPY/KRW/VND = 0, KWD/BHD/OMR/TND = 3, else 2)
 *  - Stablecoins: 2 for money figures, up to 6 for quantities
 *  - BTC: 8, ETH/EVM & other L1s: 6 display (8 exact)
 *  - Registry overrides (`token_configs`, `waas_currencies`) win over defaults
 *
 * The backend mirror lives in `supabase/functions/_shared/formatAmount.ts`
 * and MUST be kept in sync so emails/notifications read identically to the UI.
 */

const SYMBOLS: Record<string, string> = {
  // Fiat
  EUR: "€",
  USD: "$",
  GBP: "£",
  CHF: "CHF",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  // Crypto
  BTC: "₿",
  ETH: "Ξ",
  USDT: "₮",
  USDC: "$",
  TRX: "TRX",
  LTC: "Ł",
  XRP: "XRP",
  SOL: "SOL",
  XTEM: "XTEM V2",
};

// Display names for currencies (used in wallet UI)
const DISPLAY_NAMES: Record<string, string> = {
  XTEM: "XTEM V2",
};

// Dynamic symbol cache populated from token_configs
let _dynamicSymbols: Record<string, string> = {};
let _dynamicNames: Record<string, string> = {};

export function registerTokenSymbols(symbols: Record<string, string>) {
  _dynamicSymbols = { ..._dynamicSymbols, ...symbols };
}

export function registerTokenNames(names: Record<string, string>) {
  _dynamicNames = { ..._dynamicNames, ...names };
}

export function getCurrencySymbol(currency: string): string {
  const upper = currency?.toUpperCase();
  return _dynamicSymbols[upper] || SYMBOLS[upper] || currency;
}

export function getCurrencyDisplayName(currency: string): string {
  const upper = currency?.toUpperCase();
  return _dynamicNames[upper] || DISPLAY_NAMES[upper] || upper;
}

/* ------------------------------------------------------------------ */
/* Precision registry                                                  */
/* ------------------------------------------------------------------ */

/** ISO 4217 currencies whose minor unit is not 2 digits. */
const ISO_ZERO_DECIMAL = new Set([
  "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG", "RWF",
  "UGX", "UYI", "VND", "VUV", "XAF", "XOF", "XPF",
]);
const ISO_THREE_DECIMAL = new Set(["BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND"]);

const FIAT_CODES = new Set([
  "EUR", "USD", "GBP", "CHF", "JPY", "CAD", "AUD", "NZD", "SEK", "NOK", "DKK", "PLN", "CZK",
  "HUF", "RON", "BGN", "HRK", "TRY", "ZAR", "BRL", "MXN", "ARS", "CLP", "COP", "PEN", "INR",
  "IDR", "MYR", "PHP", "THB", "VND", "KRW", "SGD", "HKD", "TWD", "CNY", "RUB", "UAH", "ILS",
  "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "EGP", "NGN", "KES", "GHS", "UGX", "TZS", "XOF",
  "XAF", "BIF", "DJF", "GNF", "ISK", "KMF", "PYG", "RWF", "VUV", "XPF", "IQD", "JOD", "LYD",
  "TND", "MAD", "DZD", "JMD", "TTD", "DOP", "GTQ", "HNL", "NIO", "CRC", "PAB", "BOB", "UYU",
  "PKR", "BDT", "LKR", "NPR", "MMK", "KHR", "LAK", "MNT", "KZT", "UZS", "AZN", "GEL", "AMD",
  "MDL", "RSD", "BYN", "BWP", "MZN", "ZMW", "MWK", "AOA", "CDF", "ETB", "SDG", "SOS", "SLE",
  "GMD", "LRD", "MGA", "MRU", "MOP", "BND", "PGK", "HTG", "VES", "TJS", "TMT", "KGS", "AFN",
  "YER", "LBP", "SYP", "IRR", "FJD", "SBD", "TOP", "WST",
]);

const STABLECOINS = new Set([
  "USDT", "USDC", "BUSD", "DAI", "TUSD", "FDUSD", "USDP", "PYUSD", "USDD", "EURC", "EURT",
]);

/** Crypto assets whose market convention differs from the 6-dp default. */
const CRYPTO_DECIMALS: Record<string, number> = {
  BTC: 8,
  ETH: 6,
  BNB: 6,
  LTC: 6,
  BCH: 6,
  XMR: 6,
  ZEC: 6,
  SOL: 4,
  TRX: 6,
  XRP: 6,
  ADA: 4,
  DOGE: 4,
  MATIC: 4,
  AVAX: 4,
  DOT: 4,
  SHIB: 0,
};

/** Registry-sourced overrides (token_configs / waas_currencies / fiat_currencies). */
const _registryDecimals: Record<string, number> = {};
const _registryQuantityDecimals: Record<string, number> = {};
const _registryPriceDecimals: Record<string, number> = {};
const _registryFiatCodes = new Set<string>();

export interface CurrencyPrecisionEntry {
  currency: string;
  /** Display decimals for balances/money. */
  decimals?: number | null;
  /** Decimals for trade quantities. */
  quantityDecimals?: number | null;
  /** Decimals for prices. */
  priceDecimals?: number | null;
  /** Mark the code as fiat (from the fiat registry). */
  isFiat?: boolean;
}

/** Register precision coming from the database registries. */
export function registerCurrencyPrecision(entries: CurrencyPrecisionEntry[]) {
  for (const e of entries) {
    const cur = (e.currency || "").toUpperCase();
    if (!cur) continue;
    if (e.isFiat) _registryFiatCodes.add(cur);
    if (Number.isFinite(e.decimals as number)) {
      // Chain-level decimals (18 for EVM tokens) are not a display precision —
      // cap them at the market-standard maximum so UI never renders 18 dp.
      _registryDecimals[cur] = Math.min(Math.max(Number(e.decimals), 0), 8);
    }
    if (Number.isFinite(e.quantityDecimals as number)) {
      _registryQuantityDecimals[cur] = Math.min(Math.max(Number(e.quantityDecimals), 0), 8);
    }
    if (Number.isFinite(e.priceDecimals as number)) {
      _registryPriceDecimals[cur] = Math.min(Math.max(Number(e.priceDecimals), 0), 8);
    }
  }
}

/** True when the code is a fiat currency (static list or registered registry). */
export function isFiatCurrency(currency: string): boolean {
  const upper = (currency || "").toUpperCase();
  return FIAT_CODES.has(upper) || _registryFiatCodes.has(upper);
}

export function isStablecoin(currency: string): boolean {
  return STABLECOINS.has((currency || "").toUpperCase());
}

/** Maximum decimals an amount of this currency may carry (input/quantization). */
export function getAmountDecimals(currency: string): number {
  const upper = (currency || "").toUpperCase();
  if (isFiatCurrency(upper)) return getFiatDecimals(upper);
  return _registryQuantityDecimals[upper] ?? _registryDecimals[upper] ?? (upper === "BTC" ? 8 : 8);
}

/** ISO 4217 minor units for a fiat code. */
export function getFiatDecimals(currency: string): number {
  const upper = (currency || "").toUpperCase();
  if (ISO_ZERO_DECIMAL.has(upper)) return 0;
  if (ISO_THREE_DECIMAL.has(upper)) return 3;
  return 2;
}

/** Display precision for balances and money figures of an asset. */
export function getDisplayDecimals(currency: string): number {
  const upper = (currency || "").toUpperCase();
  if (isFiatCurrency(upper)) return getFiatDecimals(upper);
  if (_registryDecimals[upper] !== undefined) return _registryDecimals[upper];
  if (STABLECOINS.has(upper)) return 2;
  if (CRYPTO_DECIMALS[upper] !== undefined) return CRYPTO_DECIMALS[upper];
  return 6;
}

/** Precision for trade/order quantities (dust-trimmed on display). */
export function getQuantityDecimals(currency: string): number {
  const upper = (currency || "").toUpperCase();
  if (_registryQuantityDecimals[upper] !== undefined) return _registryQuantityDecimals[upper];
  if (isFiatCurrency(upper)) return getFiatDecimals(upper);
  if (STABLECOINS.has(upper)) return 6;
  return getDisplayDecimals(upper);
}

/** Precision for prices quoted in a currency. */
export function getPriceDecimals(currency: string, price?: number): number {
  const upper = (currency || "").toUpperCase();
  if (_registryPriceDecimals[upper] !== undefined) return _registryPriceDecimals[upper];
  const p = Math.abs(Number(price ?? 0));
  if (!Number.isFinite(p) || p === 0) return 2;
  if (p >= 1000) return 2;
  if (p >= 1) return 4;
  if (p >= 0.01) return 6;
  return 8;
}

/* ------------------------------------------------------------------ */
/* Formatters                                                          */
/* ------------------------------------------------------------------ */

function group(value: number, min: number, max: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });
}

function toNumber(value: number | string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format an amount at its asset precision with trailing zeros trimmed, so
 * values like 267.85714285714283 render as 267.857143 rather than raw float dust.
 */
export function formatAssetAmount(value: number | string | null | undefined, currency: string): string {
  const n = toNumber(value);
  const dp = getDisplayDecimals(currency);
  const fixed = n.toFixed(dp);
  const trimmed = dp > 0 && fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
  const [int, dec] = trimmed.split(".");
  const grouped = Number(int).toLocaleString("en-US");
  return dec ? `${grouped}.${dec}` : grouped;
}

/**
 * Canonical money renderer: NO currency glyph, plain grouped number with a
 * trailing currency code (e.g. "71.428571 USDT", "-250.00 USD").
 * Money keeps its trailing zeros — `1,250.00 USD`, never `1,250 USD`.
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency?: string | null,
  opts?: { code?: boolean; sign?: string; decimals?: number; minDecimals?: number },
): string {
  const safe = toNumber(value);
  const cur = (currency || "").toUpperCase();
  const max = opts?.decimals ?? getDisplayDecimals(cur);
  const min = opts?.minDecimals ?? max;
  const num = group(safe, Math.min(min, max), max);
  const showCode = opts?.code !== false && !!cur;
  return `${opts?.sign ?? ""}${num}${showCode ? ` ${cur}` : ""}`;
}

/** Fiat money at ISO minor units, with an optional currency symbol prefix. */
export function formatFiat(
  value: number | string | null | undefined,
  currency = "USD",
  opts?: { symbol?: boolean; code?: boolean },
): string {
  const cur = (currency || "USD").toUpperCase();
  const dp = getFiatDecimals(cur);
  const num = group(toNumber(value), dp, dp);
  const prefix = opts?.symbol ? getCurrencySymbol(cur) : "";
  const suffix = opts?.code ? ` ${cur}` : "";
  return `${prefix}${num}${suffix}`;
}

/** Trade/order quantity — asset precision, dust trimmed. */
export function formatQuantity(value: number | string | null | undefined, currency: string): string {
  const n = toNumber(value);
  const dp = getQuantityDecimals(currency);
  const fixed = n.toFixed(dp);
  const trimmed = dp > 0 && fixed.includes(".") ? fixed.replace(/\.?0+$/, "") : fixed;
  const [int, dec] = trimmed.split(".");
  return dec ? `${Number(int).toLocaleString("en-US")}.${dec}` : Number(int).toLocaleString("en-US");
}

/** Market price with precision scaled to the magnitude of the value. */
export function formatPrice(
  value: number | string | null | undefined,
  quoteCurrency = "USD",
  opts?: { symbol?: boolean },
): string {
  const n = toNumber(value);
  const dp = getPriceDecimals(quoteCurrency, n);
  const num = group(n, dp, dp);
  return opts?.symbol ? `${getCurrencySymbol(quoteCurrency)}${num}` : num;
}

/** Exact stored amount — full precision, no rounding (tx detail, exports). */
export function formatExact(value: number | string | null | undefined, currency?: string): string {
  const raw = typeof value === "string" ? value : String(toNumber(value));
  // Strip float dust while preserving every meaningful digit the row holds.
  const n = Number(raw);
  if (!Number.isFinite(n)) return "0";
  const fixed = n.toFixed(18).replace(/0+$/, "").replace(/\.$/, "");
  return currency ? `${fixed} ${currency.toUpperCase()}` : fixed;
}

/** Percentage with a fixed 2-dp market convention. */
export function formatPercent(value: number | string | null | undefined, opts?: { sign?: boolean }): string {
  const n = toNumber(value);
  const sign = opts?.sign && n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

/** USD portfolio/base-currency total — always 2 dp with a symbol. */
export function formatUsd(value: number | string | null | undefined, symbol = "$"): string {
  return `${symbol}${group(toNumber(value), 2, 2)}`;
}

/** Round an amount down to the asset's allowed precision before submitting. */
export function quantizeAmount(value: number | string | null | undefined, currency: string): number {
  const n = toNumber(value);
  const dp = getAmountDecimals(currency);
  const factor = Math.pow(10, dp);
  return Math.floor(Math.abs(n) * factor) / factor * (n < 0 ? -1 : 1);
}

/** `step` attribute for a numeric input of this asset. */
export function amountInputStep(currency: string): string {
  const dp = getAmountDecimals(currency);
  return dp === 0 ? "1" : `0.${"0".repeat(dp - 1)}1`;
}
